import { Injectable } from '@angular/core';
import firebase from 'firebase';
import { parseDicomdirBuffer } from './dicomdir-parser';
import { environment } from '../../environments/environment';

declare function dumpFile(file: any, option: string): [];

/** Chunk ArrayBuffer to base64 (avoids stack overflow for large buffers). */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

@Injectable({
  providedIn: 'root',
})
export class DicomUtilitiesService {
  constructor() {}

  getBodyAttributes(attributes: string[]): string[] {
    const attributesOutput = [];
    attributes.forEach(attrValue => {
      attributesOutput.push(window.document.body.getAttribute(attrValue));
      window.document.body.setAttribute(attrValue, '');
    });
    return attributesOutput;
  }

  /**
   * Get DICOMDIR metadata from backend (gcloud-deployed HTTP function). Use this for the DICOM disk upload flow.
   */
  getDicomdirMetadataFromBackend(file: File): Promise<{ studiesData: any[]; filesData: any[] }> {
    const bufferPromise =
      typeof file.arrayBuffer === 'function'
        ? file.arrayBuffer()
        : new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
          });
    return bufferPromise.then(buffer => {
      const dicomdirBase64 = arrayBufferToBase64(buffer);
      const url = environment.config.parseDicomdirForUploadUrl;
      const user = firebase.auth().currentUser;
      const idTokenPromise = user ? user.getIdToken() : Promise.resolve(null);
      return idTokenPromise.then(idToken => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }
        return fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ data: { dicomdirBase64 } }),
        }).then(res => {
          if (!res.ok) {
            return res.json().then((err: any) => Promise.reject(new Error(err?.error?.message || res.statusText)));
          }
          return res.json();
        });
      }).then((body: { result?: { studiesData?: any[]; filesData?: any[] } }) => ({
        studiesData: body.result?.studiesData ?? [],
        filesData: body.result?.filesData ?? [],
      }));
    });
  }

  getDicomdirMetadata(file: File): Promise<{ studiesData: any[]; filesData: any[] }> {
    const bufferPromise =
      typeof file.arrayBuffer === 'function'
        ? file.arrayBuffer()
        : new Promise<ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
          });
    return bufferPromise.then(buffer => parseDicomdirBuffer(buffer));
  }

  getDICOMFileData(file: File): Promise<any> {
    return new Promise(async resolve => {
      dumpFile(file, 'StudyInstanceUID');
      setTimeout(() => resolve({ data: window.document.body.getAttribute('studiesData') }), 1000);
    });
  }
}
